import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import {
  AlertCircle,
  CheckCircle,
  User as UserIcon,
  Search,
  Edit3,
  Plus,
  Trash2,
  Shield,
  UserX,
  Building2,
  Cloud,
  Loader2,
} from 'lucide-react';
import { ReactComponent as NamespaceIcon } from '../assets/icons/namespace.svg';
import { apiClient, InventoryNamespace, OwnedCloudResource, CloudResource } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { useCloudResourceFilter } from '../hooks/useCloudResourceFilter';
import { CategoryFilterDropdown } from '../components/CategoryFilterDropdown';

interface CurrentUser {
  user_id: string;
  email: string;
  role: string;
  tenant_id: string;
  created_at: number;
}

const Settings: React.FC = () => {
  const { isLoading: userLoading, user: authUser } = useAuth();
  // Namespace / cloud-resource ownership exists solely for incident
  // assignment, so it's hidden with the legacy Incidents product unless the
  // tenant has it re-enabled.
  const legacyProductsEnabled = authUser?.legacy_products_enabled === true;
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Tenant (organization) editing state
  const [tenantName, setTenantName] = useState('');
  const [isEditingTenantName, setIsEditingTenantName] = useState(false);
  const [tenantLoading, setTenantLoading] = useState(false);

  // Email editing state
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');

  // 2FA state
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [is2FASetupOpen, setIs2FASetupOpen] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [setupStep, setSetupStep] = useState<'qr' | 'verify'>('qr');
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [verifying2FA, setVerifying2FA] = useState(false);
  const [modalError, setModalError] = useState('');

  // Disable 2FA state
  const [disable2FAOpen, setDisable2FAOpen] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableTotpCode, setDisableTotpCode] = useState('');
  const [disabling2FA, setDisabling2FA] = useState(false);
  const [useBackupCodeDisable, setUseBackupCodeDisable] = useState(false);

  // Namespace state
  const [ownedNamespaces, setOwnedNamespaces] = useState<any[]>([]);
  const [availableNamespaces, setAvailableNamespaces] = useState<InventoryNamespace[]>([]);
  const [loadingNamespaces, setLoadingNamespaces] = useState(true);
  const [namespaceError, setNamespaceError] = useState('');
  const [isRequestDialogOpen, setIsRequestDialogOpen] = useState(false);
  const [namespaceSearchTerm, setNamespaceSearchTerm] = useState('');
  const [selectedNamespace, setSelectedNamespace] = useState<InventoryNamespace | null>(null);
  const [requestingOwnership, setRequestingOwnership] = useState(false);

  // Give up ownership state
  const [giveUpDialogOpen, setGiveUpDialogOpen] = useState(false);
  const [namespaceToGiveUp, setNamespaceToGiveUp] = useState<any | null>(null);
  const [givingUpOwnership, setGivingUpOwnership] = useState(false);

  // Cloud resource ownership state
  const [ownedCloudResources, setOwnedCloudResources] = useState<OwnedCloudResource[]>([]);
  const [loadingCloudResources, setLoadingCloudResources] = useState(true);
  const [cloudResourceError, setCloudResourceError] = useState('');
  const [isCloudResourceRequestDialogOpen, setIsCloudResourceRequestDialogOpen] = useState(false);
  const cloudFilter = useCloudResourceFilter({
    pageSize: 20,
    enabled: isCloudResourceRequestDialogOpen,
  });
  const [selectedCloudResource, setSelectedCloudResource] = useState<CloudResource | null>(null);
  const [requestingCloudResourceOwnership, setRequestingCloudResourceOwnership] = useState(false);

  // Cloud resource give up ownership state
  const [cloudResourceGiveUpDialogOpen, setCloudResourceGiveUpDialogOpen] = useState(false);
  const [cloudResourceToGiveUp, setCloudResourceToGiveUp] = useState<OwnedCloudResource | null>(null);
  const [givingUpCloudResourceOwnership, setGivingUpCloudResourceOwnership] = useState(false);

  // Namespace pagination state
  const [namespacePage, setNamespacePage] = useState(1);
  const [namespaceTotalPages, setNamespaceTotalPages] = useState(1);
  const [, setNamespaceTotalCount] = useState(0);
  const namespacesPerPage = 20;
  const [debouncedNamespaceSearch, setDebouncedNamespaceSearch] = useState('');
  const [namespaceDialogLoading, setNamespaceDialogLoading] = useState(false);

  // Delete account state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);

  // Delete tenant state
  const [deleteTenantDialogOpen, setDeleteTenantDialogOpen] = useState(false);
  const [deleteTenantConfirmText, setDeleteTenantConfirmText] = useState('');
  const [deletingTenant, setDeletingTenant] = useState(false);

  useEffect(() => {
    fetchCurrentUser();
    fetch2FAStatus();
  }, []);

  // Ownership data only feeds the incident-routing ownership cards, so skip
  // the calls entirely when those cards don't render (legacy products hidden
  // or a role that never sees them).
  const ownershipCardsEnabled = legacyProductsEnabled &&
    (currentUser?.role === 'app_developer' || currentUser?.role === 'ops_admin');

  useEffect(() => {
    if (currentUser) {
      if (ownershipCardsEnabled) {
        fetchOwnedNamespaces();
        fetchAvailableNamespaces(1, '');
        fetchOwnedCloudResources();
      } else {
        setLoadingNamespaces(false);
        setLoadingCloudResources(false);
      }
      fetchCurrentTenant();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, ownershipCardsEnabled]);

  // Debounce namespace search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedNamespaceSearch(namespaceSearchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [namespaceSearchTerm]);

  // Fetch namespaces when debounced search changes
  useEffect(() => {
    if (isRequestDialogOpen) {
      setNamespacePage(1);
      fetchAvailableNamespaces(1, debouncedNamespaceSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedNamespaceSearch, isRequestDialogOpen]);

  // Fallback to stop loading if user is loaded but currentUser is null
  useEffect(() => {
    if (!userLoading && !currentUser) {
      setLoadingNamespaces(false);
      setLoadingCloudResources(false);
    }
  }, [userLoading, currentUser]);

  // Safety timeout to prevent infinite loading
  useEffect(() => {
    if (loadingNamespaces) {
      const timeout = setTimeout(() => {
        setLoadingNamespaces(false);
      }, 10000); // 10 second timeout

      return () => clearTimeout(timeout);
    }
  }, [loadingNamespaces]);

  // Safety timeout for cloud resources
  useEffect(() => {
    if (loadingCloudResources) {
      const timeout = setTimeout(() => {
        setLoadingCloudResources(false);
      }, 10000); // 10 second timeout

      return () => clearTimeout(timeout);
    }
  }, [loadingCloudResources]);

  const fetchCurrentUser = async () => {
    setLoading(true);
    try {
      const response = await apiClient.getCurrentUser();
      setCurrentUser(response);
      setNewEmail(response.email);
    } catch (err) {
      setError('Failed to load user information');
    } finally {
      setLoading(false);
    }
  };

  const fetch2FAStatus = async () => {
    try {
      const status = await apiClient.get2FAStatus();
      setIs2FAEnabled(status.enabled && status.verified);
    } catch (err: any) {
      console.error('Failed to fetch 2FA status:', err);
    }
  };

  const fetchCurrentTenant = async () => {
    try {
      setTenantLoading(true);
      const t = await apiClient.getTenant();
      setTenantName(t.name || '');
    } catch (err) {
      // Non-fatal for page
    } finally {
      setTenantLoading(false);
    }
  };

  const fetchOwnedNamespaces = async () => {
    setLoadingNamespaces(true);
    setNamespaceError('');
    
    if (!currentUser) {
      setOwnedNamespaces([]);
      setLoadingNamespaces(false);
      return;
    }
    
    try {
      // Make the API call to get user's owned namespaces
      const ownedResponse = await apiClient.listMyNamespaces();
      
      // Get clusters for enrichment
      const clustersResponse = await apiClient.getInventoryClusters();
      
      // Handle the response structure (it should have a 'namespaces' field)
      const namespaces = ownedResponse?.namespaces || [];
      
      if (!Array.isArray(namespaces)) {
        setOwnedNamespaces([]);
        return;
      }
      
      // Create cluster lookup map for enrichment
      const clusterMap = new Map();
      if (clustersResponse?.clusters) {
        clustersResponse.clusters.forEach(cluster => {
          // Map both 'id' and 'uid' fields to handle different response formats
          clusterMap.set(cluster.id, cluster.name);
          clusterMap.set(cluster.uid, cluster.name);
        });
      }
      
      // Enrich namespaces with cluster names
      const enrichedNamespaces = namespaces.map(ns => {
        const clusterName = ns.cluster_name || 
                           clusterMap.get(ns.cluster_id) || 
                           clusterMap.get(ns.cluster_uid) || 
                           'Unknown';
        
        return {
          ...ns,
          cluster_name: clusterName
        };
      });
      
      setOwnedNamespaces(enrichedNamespaces);
      
    } catch (error: any) {
      // An expired trial / missing subscription isn't a page error — show the
      // empty state silently instead of a banner.
      if (error?.response?.data?.error !== 'subscription_required') {
        setNamespaceError('Failed to load your owned namespaces');
      }
      setOwnedNamespaces([]);
    } finally {
      setLoadingNamespaces(false);
    }
  };

  const fetchAvailableNamespaces = useCallback(async (page: number = 1, search: string = '') => {
    setNamespaceDialogLoading(true);
    try {
      const response = await apiClient.getInventoryNamespacesPaginated({
        page,
        page_size: namespacesPerPage,
        search: search || undefined,
      });
      setAvailableNamespaces(response?.namespaces || []);
      setNamespaceTotalCount(response?.total_count || 0);
      setNamespaceTotalPages(response?.total_pages || 1);
      setNamespacePage(response?.page || page);
    } catch (err: any) {
      setAvailableNamespaces([]);
      // Only set error if we don't already have a namespace error, and never
      // for a missing subscription (the empty state covers it).
      if (!namespaceError && err?.response?.data?.error !== 'subscription_required') {
        setNamespaceError('Failed to load available namespaces for requests');
      }
    } finally {
      setNamespaceDialogLoading(false);
    }
  }, [namespacesPerPage, namespaceError]);

  // Cloud resource ownership functions
  const fetchOwnedCloudResources = async () => {
    setLoadingCloudResources(true);
    setCloudResourceError('');

    if (!currentUser) {
      setOwnedCloudResources([]);
      setLoadingCloudResources(false);
      return;
    }

    try {
      const response = await apiClient.listMyCloudResources();
      setOwnedCloudResources(response?.cloud_resources || []);
    } catch (error: any) {
      // An expired trial / missing subscription isn't a page error — show the
      // empty state silently instead of a banner.
      if (error?.response?.data?.error !== 'subscription_required') {
        setCloudResourceError('Failed to load your owned cloud resources');
      }
      setOwnedCloudResources([]);
    } finally {
      setLoadingCloudResources(false);
    }
  };

  const handleRequestCloudResourceOwnership = async () => {
    if (!selectedCloudResource || !currentUser) return;

    setRequestingCloudResourceOwnership(true);
    setCloudResourceError('');
    try {
      if (currentUser.role === 'ops_admin') {
        // ops_admin can directly claim ownership without approval
        await apiClient.assignCloudResourceOwner({ user_id: currentUser.user_id, cloud_resource_id: selectedCloudResource.id });
        setSuccess('Cloud resource ownership assigned successfully.');
      } else {
        await apiClient.requestCloudResourceOwnership({ cloud_resource_id: selectedCloudResource.id });
        setSuccess('Ownership request submitted; awaiting approval');
      }
      setIsCloudResourceRequestDialogOpen(false);
      setSelectedCloudResource(null);
      cloudFilter.reset();
      fetchOwnedCloudResources();
    } catch (error: any) {
      setCloudResourceError(error?.response?.data || error?.message || 'Failed to claim cloud resource ownership');
    } finally {
      setRequestingCloudResourceOwnership(false);
    }
  };

  const handleGiveUpCloudResourceOwnership = async () => {
    if (!cloudResourceToGiveUp || !currentUser) return;

    setGivingUpCloudResourceOwnership(true);
    setCloudResourceError('');
    try {
      await apiClient.removeCloudResourceOwner({
        user_id: currentUser.user_id,
        cloud_resource_id: cloudResourceToGiveUp.id
      });
      setSuccess('Ownership relinquished');
      setCloudResourceGiveUpDialogOpen(false);
      setCloudResourceToGiveUp(null);
      fetchOwnedCloudResources();
    } catch (error: any) {
      setCloudResourceError(error?.response?.data || error?.message || 'Failed to give up ownership');
    } finally {
      setGivingUpCloudResourceOwnership(false);
    }
  };

  const handleUpdateEmail = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    // Validate email format
    if (!newEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    // Personal-email enforcement is a server-side decision (ENFORCE_WORK_EMAIL);
    // any rejection surfaces via the API error below.
    try {
      await apiClient.updateUserProfile({ email: newEmail });
      setSuccess('Email updated successfully');
      setIsEditingEmail(false);
      fetchCurrentUser();
    } catch (err: any) {
      setError(err.message || 'Failed to update email');
    } finally {
      setLoading(false);
    }
  };

  const handleSetup2FA = async () => {
    setModalError('');
    setIs2FASetupOpen(true);
    setQrCodeUrl(''); // Reset QR code to show loading state

    try {
      const response = await apiClient.setup2FAInitiate();
      console.log('2FA Setup Response:', response);

      // Handle both qr_code_url and qr_code fields
      let qrCodeSrc = '';
      if (response.qr_code_url) {
        qrCodeSrc = response.qr_code_url;
      } else if (response.qr_code) {
        // Convert raw base64 to data URI
        qrCodeSrc = `data:image/png;base64,${response.qr_code}`;
      }

      console.log('Final QR Code Source:', qrCodeSrc);
      setQrCodeUrl(qrCodeSrc);
      setSetupStep('qr');
    } catch (err: any) {
      setModalError(err.message || 'Failed to initiate 2FA setup');
      setIs2FASetupOpen(false); // Close modal on error
    }
  };

  const handleVerify2FASetup = async () => {
    if (!verificationCode.trim()) {
      setModalError('Please enter the verification code');
      return;
    }

    setVerifying2FA(true);
    setModalError('');
    try {
      const response = await apiClient.setup2FAVerify({ code: verificationCode });
      setBackupCodes(response.backup_codes);
      setShowBackupCodes(true);
      setIs2FAEnabled(true);
      setSuccess('2FA has been successfully enabled for your account');
      setIs2FASetupOpen(false);
      setVerificationCode('');
      setModalError('');
    } catch (err: any) {
      setModalError(err.message || 'Invalid verification code');
    } finally {
      setVerifying2FA(false);
    }
  };

  const handleDisable2FA = () => {
    setModalError('');
    setDisablePassword('');
    setDisableTotpCode('');
    setUseBackupCodeDisable(false);
    setDisable2FAOpen(true);
  };

  const handleConfirmDisable2FA = async () => {
    if (!disablePassword.trim()) {
      setModalError('Please enter your password');
      return;
    }
    if (!disableTotpCode.trim()) {
      setModalError(useBackupCodeDisable ? 'Please enter your backup code' : 'Please enter your 2FA code');
      return;
    }

    setDisabling2FA(true);
    setModalError('');
    try {
      await apiClient.disable2FA({
        current_password: disablePassword,
        code: disableTotpCode,
        is_backup_code: useBackupCodeDisable
      });
      setIs2FAEnabled(false);
      setSuccess('2FA has been successfully disabled');
      setDisable2FAOpen(false);
      setDisablePassword('');
      setDisableTotpCode('');
    } catch (err: any) {
      setModalError(err.message || 'Failed to disable 2FA');
    } finally {
      setDisabling2FA(false);
    }
  };

  const handleCancelEmailEdit = () => {
    setNewEmail(currentUser?.email || '');
    setIsEditingEmail(false);
  };

  const handleUpdateTenantName = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await apiClient.updateTenant({ name: tenantName });
      setSuccess('Organization name updated successfully');
      setIsEditingTenantName(false);
      // Refresh tenant to reflect canonical value returned from server if needed
      fetchCurrentTenant();
    } catch (err: any) {
      setError(err.message || 'Failed to update organization name');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelTenantEdit = () => {
    setIsEditingTenantName(false);
    // Reset to last known good value
    fetchCurrentTenant();
  };

  const handleRequestOwnership = async () => {
    if (!selectedNamespace || !currentUser) return;

    setRequestingOwnership(true);
    try {
      if (currentUser.role === 'ops_admin') {
        // ops_admin can directly claim ownership without approval
        await apiClient.assignNamespaceOwner({ user_id: currentUser.user_id, namespace_id: selectedNamespace.id });
        setSuccess('Namespace ownership assigned successfully.');
      } else {
        await apiClient.requestNamespaceOwnership({ namespace_id: selectedNamespace.id });
        setSuccess('Ownership request submitted successfully! An admin will review your request.');
      }

      setIsRequestDialogOpen(false);
      setSelectedNamespace(null);
      setNamespaceSearchTerm('');
      fetchOwnedNamespaces();
    } catch (error: any) {
      setError(error.message || 'Failed to claim namespace ownership');
    } finally {
      setRequestingOwnership(false);
    }
  };

  const handleGiveUpOwnership = async () => {
    if (!namespaceToGiveUp || !currentUser) return;
    
    setGivingUpOwnership(true);
    try {
      await apiClient.removeNamespaceOwner({ 
        user_id: currentUser.user_id, 
        namespace_id: namespaceToGiveUp.id 
      });
      setSuccess(`Successfully gave up ownership of "${namespaceToGiveUp.name}"`);
      setGiveUpDialogOpen(false);
      setNamespaceToGiveUp(null);
      // Refresh the owned namespaces list
      fetchOwnedNamespaces();
    } catch (error: any) {
      setError(error.message || 'Failed to give up ownership');
    } finally {
      setGivingUpOwnership(false);
    }
  };

  const openGiveUpDialog = (namespace: any) => {
    setError('');
    setSuccess('');
    setNamespaceToGiveUp(namespace);
    setGiveUpDialogOpen(true);
  };

  const handleCancelGiveUp = () => {
    setGiveUpDialogOpen(false);
    setNamespaceToGiveUp(null);
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'delete') return;
    
    setDeletingAccount(true);
    try {
      await apiClient.deleteCurrentUser();
      // Redirect to login page after successful deletion
      window.location.href = '/login';
    } catch (error: any) {
      // Safely extract a string error message from response
      let errorMessage = 'Failed to delete account';
      const data = error.response?.data;
      if (typeof data === 'string') {
        errorMessage = data;
      } else if (data?.message && typeof data.message === 'string') {
        errorMessage = data.message;
      } else if (error.message && typeof error.message === 'string') {
        errorMessage = error.message;
      } else if (data) {
        try {
          errorMessage = JSON.stringify(data);
        } catch {
          // Keep default fallback
        }
      }
      setError(errorMessage);
      setDeleteDialogOpen(false);
      setDeleteConfirmText('');
    } finally {
      setDeletingAccount(false);
    }
  };

  const openDeleteDialog = () => {
    setError('');
    setSuccess('');
    setDeleteConfirmText('');
    setDeleteDialogOpen(true);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setDeleteConfirmText('');
  };

  const handleDeleteTenant = async () => {
    if (deleteTenantConfirmText !== 'delete') return;
    
    setDeletingTenant(true);
    try {
      await apiClient.deleteCurrentTenant();
      // Redirect to login page after successful deletion
      window.location.href = '/login';
    } catch (error: any) {
      setError(error.message || 'Failed to delete company account');
      setDeleteTenantDialogOpen(false);
      setDeleteTenantConfirmText('');
    } finally {
      setDeletingTenant(false);
    }
  };

  const openDeleteTenantDialog = () => {
    setError('');
    setSuccess('');
    setDeleteTenantConfirmText('');
    setDeleteTenantDialogOpen(true);
  };

  const handleCancelDeleteTenant = () => {
    setDeleteTenantDialogOpen(false);
    setDeleteTenantConfirmText('');
  };

  const formatRole = (role: string) => {
    if (role === 'ops_admin') return 'Platform Admin';
    return role.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Create a set of owned namespace IDs for efficient lookup
  const ownedNamespaceIds = new Set(ownedNamespaces.map(ns => ns.id));

  // Single page-level gate: for roles that render the ownership cards
  // (app_developer/ops_admin), also wait for the initial namespace and cloud
  // resource loads so the page shows exactly one centered spinner instead of
  // several staggered ones.
  const rendersOwnershipCards = legacyProductsEnabled && !!currentUser &&
    (currentUser.role === 'app_developer' || currentUser.role === 'ops_admin');
  const initialOwnershipLoading = rendersOwnershipCards && (loadingNamespaces || loadingCloudResources);

  if (userLoading || loading || initialOwnershipLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Failed to load user information</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Error and Success Messages */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      {success && (
        <Alert className="border-green-200 bg-green-50 dark:border-green-800/30 dark:bg-green-900/20">
          <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
          <AlertDescription className="text-green-700 dark:text-green-400">
            {success}
          </AlertDescription>
        </Alert>
      )}

      {namespaceError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{namespaceError}</AlertDescription>
        </Alert>
      )}

      {cloudResourceError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{cloudResourceError}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-6">
        {/* Profile Information */}
          <Card>
            <CardHeader>
              <CardTitle>
                Profile Settings
              </CardTitle>
              <CardDescription>
                Manage your account details and preferences.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Email Section */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Email Address</Label>
                {isEditingEmail ? (
                  <div className="space-y-3">
                    <Input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="Enter your email address"
                    />
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        onClick={handleUpdateEmail}
                        disabled={loading || newEmail === currentUser.email}
                      >
                        Save
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={handleCancelEmailEdit}
                        disabled={loading}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-md">
                    <span className="text-sm">{currentUser.email}</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setIsEditingEmail(true)}
                      className="h-auto p-1"
                    >
                      <Edit3 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>

              {/* Role Section */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Role</Label>
                <div className="flex items-center gap-2">
                  <Badge>
                    {formatRole(currentUser.role)}
                  </Badge>
                </div>
              </div>

              {/* Member Since */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Member Since</Label>
                <p className="text-sm text-muted-foreground">
                  {formatDate(currentUser.created_at)}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Security Settings */}
          <Card>
            <CardHeader>
              <CardTitle>
                Security
              </CardTitle>
              <CardDescription>
                Manage your account security settings including two-factor authentication.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* 2FA Section */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-medium">Two-Factor Authentication (2FA)</Label>
                  <Badge variant={is2FAEnabled ? "default" : "secondary"}>
                    {is2FAEnabled ? "Enabled" : "Disabled"}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  Add an extra layer of security to your account using an authenticator app.
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <Button
                    size="sm"
                    variant={is2FAEnabled ? "destructive" : "default"}
                    onClick={is2FAEnabled ? handleDisable2FA : handleSetup2FA}
                  >
                    {is2FAEnabled ? "Disable 2FA" : "Setup 2FA"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Organization Settings - Only for org_admins */}
          {currentUser.role === 'org_admin' && (
            <Card>
              <CardHeader>
                <CardTitle>
                  Organization Settings
                </CardTitle>
                <CardDescription>
                  Manage your tenant name as shown across the platform.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Organization Name</Label>
                  {isEditingTenantName ? (
                    <div className="space-y-3">
                      <Input
                        value={tenantName}
                        onChange={(e) => setTenantName(e.target.value)}
                        placeholder="Enter organization name"
                        disabled={tenantLoading}
                      />
                      <p className="text-xs text-muted-foreground">
                        Allowed characters: letters, numbers, underscores, dots, and hyphens. Max 100 characters.
                      </p>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          onClick={handleUpdateTenantName}
                          disabled={loading || tenantLoading}
                        >
                          Save
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={handleCancelTenantEdit}
                          disabled={loading}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-md">
                      <span className="text-sm">{tenantLoading ? 'Loading...' : (tenantName || '—')}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setIsEditingTenantName(true)}
                        className="h-auto p-1"
                        disabled={tenantLoading}
                      >
                        <Edit3 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        

          {/* Delete Account */}
          <Card>
            <CardHeader>
              <CardTitle>
                Delete Account
              </CardTitle>
              <CardDescription>
                Permanently delete your account and all associated data. This action cannot be undone.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                This will permanently delete your account, including all chat history and namespace ownership. You will be logged out immediately.
              </p>
              {currentUser.role === 'org_admin' && (
                <p className="text-sm text-muted-foreground mb-4">
                  As an organization admin, you can only delete your account if there is at least one other organization admin. Otherwise, you must transfer admin privileges first or delete the entire organization below.
                </p>
              )}
              <Button
                variant="destructive"
                size="sm"
                onClick={openDeleteDialog}
              >
                Delete My Account
              </Button>
            </CardContent>
          </Card>

          {/* Delete Organization Account */}
          {currentUser.role === 'org_admin' && (
            <Card>
              <CardHeader>
                <CardTitle>
                  Delete Organization Account
                </CardTitle>
                <CardDescription>
                  Permanently delete the entire organization account and all associated data. This action cannot be undone.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  This will permanently delete your entire organization account, including all users, clusters, network policies, chat history, assessments, and organizational settings. Everyone will be logged out immediately and all access will be revoked.
                </p>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={openDeleteTenantDialog}
                >
                  Delete Organization Account
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Namespace Ownership */}
          {legacyProductsEnabled && (currentUser.role === 'app_developer' || currentUser.role === 'ops_admin') && (
          <>
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>
                    My Namespaces
                  </CardTitle>
                  <CardDescription>
                    Namespaces you have ownership permissions for.
                  </CardDescription>
                </div>
                <Dialog open={isRequestDialogOpen} onOpenChange={setIsRequestDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="outline">
                      <Plus className="h-4 w-4 mr-2" />
                      {currentUser?.role === 'ops_admin' ? 'Claim Ownership' : 'Request Access'}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>{currentUser?.role === 'ops_admin' ? 'Claim Namespace Ownership' : 'Request Namespace Ownership'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search namespaces..."
                          value={namespaceSearchTerm}
                          onChange={(e) => setNamespaceSearchTerm(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                      <div className="max-h-96 overflow-y-auto space-y-2">
                        {namespaceDialogLoading ? (
                          <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                          </div>
                        ) : availableNamespaces.filter(ns => !ownedNamespaceIds.has(ns.id)).length > 0 ? (
                          availableNamespaces
                            .filter(ns => !ownedNamespaceIds.has(ns.id))
                            .map((ns) => (
                              <div
                                key={ns.id}
                                onClick={() => setSelectedNamespace(ns)}
                                className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                                  selectedNamespace?.id === ns.id
                                    ? 'border-primary bg-primary/5'
                                    : 'border-border hover:border-primary/50 hover:bg-muted/50'
                                }`}
                              >
                                <p className="font-medium">{ns.name}</p>
                                <p className="text-sm text-muted-foreground">
                                  Cluster: {ns.cluster_name || 'Unknown'}
                                </p>
                              </div>
                            ))
                        ) : (
                          <div className="text-center py-8">
                            <NamespaceIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                            <p className="text-muted-foreground">
                              {namespaceSearchTerm ? 'No namespaces found matching your search.' : 'No namespaces available.'}
                            </p>
                          </div>
                        )}
                      </div>
                      {/* Pagination controls */}
                      {(() => {
                        const filteredOnPage = availableNamespaces.filter(ns => !ownedNamespaceIds.has(ns.id));
                        const visibleCount = filteredOnPage.length;
                        const showPagination = namespaceTotalPages > 1 && !namespaceDialogLoading;
                        if (!showPagination) return null;
                        return (
                          <div className="flex items-center justify-between pt-2">
                            <p className="text-sm text-muted-foreground">
                              Showing {visibleCount} item{visibleCount !== 1 ? 's' : ''} on this page
                            </p>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  const newPage = namespacePage - 1;
                                  setNamespacePage(newPage);
                                  fetchAvailableNamespaces(newPage, debouncedNamespaceSearch);
                                }}
                                disabled={namespacePage <= 1}
                              >
                                Previous
                              </Button>
                              <span className="text-sm py-2 px-2">
                                Page {namespacePage} of {namespaceTotalPages}
                              </span>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  const newPage = namespacePage + 1;
                                  setNamespacePage(newPage);
                                  fetchAvailableNamespaces(newPage, debouncedNamespaceSearch);
                                }}
                                disabled={namespacePage >= namespaceTotalPages}
                              >
                                Next
                              </Button>
                            </div>
                          </div>
                        );
                      })()}
                      <div className="flex justify-end space-x-2 pt-4 border-t">
                        <Button
                          variant="outline"
                          onClick={() => setIsRequestDialogOpen(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleRequestOwnership}
                          disabled={!selectedNamespace || requestingOwnership}
                        >
                          {requestingOwnership
                            ? (currentUser?.role === 'ops_admin' ? 'Claiming...' : 'Requesting...')
                            : (currentUser?.role === 'ops_admin' ? 'Claim' : 'Submit Request')}
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {loadingNamespaces ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : ownedNamespaces.length > 0 ? (
                <div className="space-y-3">
                  {ownedNamespaces.map((ns) => (
                    <div key={ns.id} className="p-4 bg-muted/30 rounded-lg border group">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{ns.name}</p>
                          <p className="text-sm text-muted-foreground">
                            Cluster: {ns.cluster_name || 'Unknown'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800/30">
                            Owner
                          </Badge>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openGiveUpDialog(ns)}
                            className="sm:opacity-0 sm:group-hover:opacity-100 transition-opacity text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                            title="Give up ownership"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <NamespaceIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">
                    You don't own any namespaces yet.
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {currentUser?.role === 'ops_admin'
                      ? 'Claim ownership so incidents for specific namespaces are assigned to you.'
                      : 'Request ownership so incidents for specific namespaces are assigned to you.'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* My Cloud Resources Card */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>
                  My Cloud Resources
                </CardTitle>
                <CardDescription>
                  Cloud resources you own and are responsible for managing
                </CardDescription>
              </div>
              <div>
                <Dialog open={isCloudResourceRequestDialogOpen} onOpenChange={setIsCloudResourceRequestDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="outline">
                      <Plus className="h-4 w-4 mr-2" />
                      {currentUser?.role === 'ops_admin' ? 'Claim Ownership' : 'Request Ownership'}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[600px] flex flex-col">
                    <DialogHeader>
                      <DialogTitle>{currentUser?.role === 'ops_admin' ? 'Claim Cloud Resource Ownership' : 'Request Cloud Resource Ownership'}</DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-col gap-4 min-h-0 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            placeholder="Search cloud resources..."
                            value={cloudFilter.searchTerm}
                            onChange={(e) => cloudFilter.setSearchTerm(e.target.value)}
                            className="pl-10"
                          />
                        </div>
                        <CategoryFilterDropdown
                          variant="inline"
                          selectedCategories={cloudFilter.selectedCategories}
                          onCategoryChange={cloudFilter.handleCategoryChange}
                          onToggleAll={cloudFilter.toggleAllCategories}
                        />
                      </div>
                      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden space-y-2">
                        {cloudFilter.loading ? (
                          <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                          </div>
                        ) : cloudFilter.resources.filter(res => !ownedCloudResources.some(owned => owned.id === res.id)).length > 0 ? (
                          cloudFilter.resources
                            .filter(res => !ownedCloudResources.some(owned => owned.id === res.id))
                            .map(res => (
                              <div
                                key={res.id}
                                onClick={() => setSelectedCloudResource(res)}
                                className={`p-3 border rounded-lg cursor-pointer transition-colors overflow-hidden ${
                                  selectedCloudResource?.id === res.id
                                    ? 'border-primary bg-primary/5'
                                    : 'border-border hover:border-primary/50 hover:bg-muted/50'
                                }`}
                              >
                                <p className="font-medium truncate">{res.name || res.arn || 'Unnamed Resource'}</p>
                                <p className="text-sm text-muted-foreground truncate">
                                  {res.resource_type} ({res.cloud_provider?.toUpperCase()})
                                </p>
                                {res.arn && (
                                  <p className="text-xs text-muted-foreground truncate">{res.arn}</p>
                                )}
                              </div>
                            ))
                        ) : (
                          <div className="text-center py-8">
                            <Cloud className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                            <p className="text-muted-foreground">
                              {cloudFilter.searchTerm ? 'No cloud resources found matching your search.' : 'No cloud resources available.'}
                            </p>
                          </div>
                        )}
                      </div>
                      {/* Pagination controls */}
                      {cloudFilter.totalPages > 1 && !cloudFilter.loading && (
                        <div className="flex items-center justify-between pt-2">
                          <p className="text-sm text-muted-foreground">
                            Showing {cloudFilter.resources.filter(res => !ownedCloudResources.some(owned => owned.id === res.id)).length} item(s) on this page
                          </p>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => cloudFilter.setPage(p => p - 1)}
                              disabled={cloudFilter.page <= 1}
                            >
                              Previous
                            </Button>
                            <span className="text-sm py-2 px-2">
                              Page {cloudFilter.page} of {cloudFilter.totalPages}
                            </span>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => cloudFilter.setPage(p => p + 1)}
                              disabled={cloudFilter.page >= cloudFilter.totalPages}
                            >
                              Next
                            </Button>
                          </div>
                        </div>
                      )}
                      <div className="flex justify-end space-x-2 pt-4 border-t">
                        <Button
                          variant="outline"
                          onClick={() => setIsCloudResourceRequestDialogOpen(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleRequestCloudResourceOwnership}
                          disabled={!selectedCloudResource || requestingCloudResourceOwnership}
                        >
                          {requestingCloudResourceOwnership
                            ? (currentUser?.role === 'ops_admin' ? 'Claiming...' : 'Requesting...')
                            : (currentUser?.role === 'ops_admin' ? 'Claim' : 'Submit Request')}
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {loadingCloudResources ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : ownedCloudResources.length > 0 ? (
                <div className="space-y-3">
                  {ownedCloudResources.map((res) => (
                    <div key={res.id} className="p-4 bg-muted/30 rounded-lg border group">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{res.name || res.arn || 'Unnamed Resource'}</p>
                          <p className="text-sm text-muted-foreground">
                            {res.resource_type} ({res.cloud_provider?.toUpperCase()})
                          </p>
                          {res.region && (
                            <p className="text-xs text-muted-foreground">Region: {res.region}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800/30">
                            Owner
                          </Badge>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setCloudResourceToGiveUp(res);
                              setCloudResourceGiveUpDialogOpen(true);
                            }}
                            className="sm:opacity-0 sm:group-hover:opacity-100 transition-opacity text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                            title="Give up ownership"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Cloud className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">
                    You don't own any cloud resources yet.
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {currentUser?.role === 'ops_admin'
                      ? 'Claim ownership so incidents for specific cloud resources are assigned to you.'
                      : 'Request ownership so incidents for specific cloud resources are assigned to you.'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
          </>
          )}
      </div>

      {/* Give Up Namespace Ownership Confirmation Dialog */}
      <AlertDialog open={giveUpDialogOpen} onOpenChange={setGiveUpDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Give Up Namespace Ownership?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to give up ownership of <strong>"{namespaceToGiveUp?.name}"</strong>? 
              Incidents triggered for this namespace will no longer be assigned to you unless you {currentUser?.role === 'ops_admin' ? 'claim' : 'request'} ownership again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelGiveUp}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleGiveUpOwnership}
              disabled={givingUpOwnership}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {givingUpOwnership ? 'Giving up...' : 'Give Up Ownership'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Give Up Cloud Resource Ownership Confirmation Dialog */}
      <AlertDialog open={cloudResourceGiveUpDialogOpen} onOpenChange={setCloudResourceGiveUpDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Give Up Cloud Resource Ownership?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to give up ownership of <strong>"{cloudResourceToGiveUp?.name || cloudResourceToGiveUp?.arn}"</strong>?
              Incidents triggered for this cloud resource will no longer be assigned to you unless you {currentUser?.role === 'ops_admin' ? 'claim' : 'request'} ownership again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setCloudResourceGiveUpDialogOpen(false);
              setCloudResourceToGiveUp(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleGiveUpCloudResourceOwnership}
              disabled={givingUpCloudResourceOwnership}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {givingUpCloudResourceOwnership ? 'Giving up...' : 'Give Up Ownership'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Account Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600 dark:text-red-400">Delete Account</AlertDialogTitle>
            <AlertDialogDescription>
              This action will permanently delete your account and all associated data, including:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Your user profile and settings</li>
                <li>All chat history</li>
                <li>Namespace ownership permissions</li>
              </ul>
              <strong className="block mt-3">This action cannot be undone.</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="delete-confirm" className="text-sm font-medium">
                To confirm deletion, type "delete" in the box below:
              </Label>
              <Input
                id="delete-confirm"
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type 'delete' to confirm"
                className="border-red-200 focus:border-red-500 dark:border-red-800 dark:focus:border-red-400"
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelDelete}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              disabled={deleteConfirmText !== 'delete' || deletingAccount}
              className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
            >
              {deletingAccount ? 'Deleting...' : 'Delete Account'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Organization Account Confirmation Dialog */}
      <AlertDialog open={deleteTenantDialogOpen} onOpenChange={setDeleteTenantDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600 dark:text-red-400">Delete Organization Account</AlertDialogTitle>
            <AlertDialogDescription>
              This action will permanently delete your entire organization account and all associated data, including:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>All users and their profiles</li>
                <li>All clusters and network policy recommendations</li>
                <li>All chat history and risk assessments</li>
                <li>All organizational settings</li>
                <li>All namespace ownership and permissions</li>
              </ul>
              <strong className="block mt-3 text-red-600 dark:text-red-400">
                This action cannot be undone and will immediately log out all users.
              </strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="delete-tenant-confirm" className="text-sm font-medium">
                To confirm deletion, type "delete" in the box below:
              </Label>
              <Input
                id="delete-tenant-confirm"
                type="text"
                value={deleteTenantConfirmText}
                onChange={(e) => setDeleteTenantConfirmText(e.target.value)}
                placeholder="Type 'delete' to confirm"
                className="border-red-200 focus:border-red-500 dark:border-red-800 dark:focus:border-red-400"
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelDeleteTenant}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTenant}
              disabled={deleteTenantConfirmText !== 'delete' || deletingTenant}
              className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
            >
              {deletingTenant ? 'Deleting...' : 'Delete Company Account'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 2FA Setup Dialog */}
      <Dialog open={is2FASetupOpen} onOpenChange={setIs2FASetupOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="text-center">
            <DialogTitle className="text-center">Setup Two-Factor Authentication</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {modalError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{modalError}</AlertDescription>
              </Alert>
            )}
            {setupStep === 'qr' ? (
              <div className="space-y-4">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-4">
                    Scan this QR code with your authenticator app (like 1Password, Google Authenticator, or Authy)
                  </p>
                  {qrCodeUrl ? (
                    <div className="flex justify-center mb-4">
                      <img
                        src={qrCodeUrl}
                        alt="2FA QR Code"
                        className="w-48 h-48 border rounded-lg shadow-sm bg-white p-2"
                        onError={(e) => {
                          console.error('Failed to load QR code image:', e);
                          console.log('QR Code URL:', qrCodeUrl);
                        }}
                      />
                    </div>
                  ) : (
                    <div className="flex justify-center mb-4">
                      <div className="w-48 h-48 border-2 border-dashed border-muted-foreground/50 rounded-lg flex flex-col items-center justify-center">
                        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground">Loading QR code...</p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="verification-code">Enter the 6-digit code from your app</Label>
                  <Input
                    id="verification-code"
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    placeholder="000000"
                    maxLength={6}
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleVerify2FASetup}
                    disabled={verifying2FA || !verificationCode.trim()}
                    className="flex-1"
                  >
                    {verifying2FA ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                        Verifying...
                      </>
                    ) : (
                      'Verify & Enable 2FA'
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIs2FASetupOpen(false);
                      setVerificationCode('');
                      setQrCodeUrl('');
                      setModalError(''); // Clear modal errors
                    }}
                    disabled={verifying2FA}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      {/* Backup Codes Display Dialog */}
      <Dialog open={showBackupCodes} onOpenChange={setShowBackupCodes}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Save Your Backup Codes</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Save these backup codes in a safe place. You can use them to access your account if you lose your authenticator device.
              </AlertDescription>
            </Alert>
            <div className="bg-muted p-4 rounded-lg">
              <div className="grid grid-cols-2 gap-2 font-mono text-sm">
                {backupCodes.map((code, index) => (
                  <div key={index} className="p-2 bg-background rounded border text-center">
                    {code}
                  </div>
                ))}
              </div>
            </div>
            <Button
              onClick={() => {
                setShowBackupCodes(false);
                setBackupCodes([]);
              }}
              className="w-full"
            >
              I've Saved These Codes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Disable 2FA Confirmation Dialog */}
      <Dialog open={disable2FAOpen} onOpenChange={setDisable2FAOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="text-center">
            <DialogTitle className="text-center text-red-600 dark:text-red-400">Disable Two-Factor Authentication</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {modalError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{modalError}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-4">
                  Are you sure you want to disable 2FA? This will make your account less secure.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="disable-password">Enter your password to confirm</Label>
                <Input
                  id="disable-password"
                  type="password"
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                  placeholder="Your account password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="disable-totp">
                  {useBackupCodeDisable ? 'Enter backup code' : 'Enter your 2FA code'}
                </Label>
                <Input
                  id="disable-totp"
                  type="text"
                  value={disableTotpCode}
                  onChange={(e) => setDisableTotpCode(e.target.value)}
                  placeholder={useBackupCodeDisable ? "8-character backup code" : "6-digit code from your authenticator app"}
                  maxLength={useBackupCodeDisable ? 9 : 6}
                />
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setUseBackupCodeDisable(!useBackupCodeDisable)}
                    className="text-sm text-muted-foreground hover:text-foreground underline"
                  >
                    {useBackupCodeDisable ? 'Use authenticator app instead' : 'Use backup code instead'}
                  </button>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleConfirmDisable2FA}
                  disabled={disabling2FA || !disablePassword.trim() || !disableTotpCode.trim()}
                  variant="destructive"
                  className="flex-1"
                >
                  {disabling2FA ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                      Disabling...
                    </>
                  ) : (
                    'Disable 2FA'
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setDisable2FAOpen(false);
                    setDisablePassword('');
                    setDisableTotpCode('');
                    setUseBackupCodeDisable(false);
                    setModalError('');
                  }}
                  disabled={disabling2FA}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Settings; 